import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from './user.service';

/**
 * Whether the API has told us that the member of staff the user is viewing as no longer exists (#1659).
 *
 * The error interceptor raises it on the API's ghost-target 404; the view-as banner in `AppComponent`
 * shows a warning while it is set. It clears whenever a view-as starts or ends (`UserService.uiImpersonation`
 * emits after both), because a new view-as, or none, has not been refused yet.
 *
 * A separate service rather than state on `UserService`, deliberately: `UserService` is on the legacy
 * lint lists, and editing it would oblige this PR to clean the whole file. Reading its signal does not.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationNoticeService implements OnDestroy {
  private readonly targetMissingSource = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();

  /**
   * True from the first ghost-target refusal until the view-as changes. Replays its current value on
   * subscribe (a BehaviorSubject): FailureNoticeService relies on that to tell a change from the replay.
   */
  public readonly targetMissing$: Observable<boolean> = this.targetMissingSource.asObservable();

  constructor(private readonly userService: UserService) {
    userService.uiImpersonation.pipe(takeUntil(this.destroy$)).subscribe(() => this.clear());
  }

  /** Record that the API refused the current view-as target as not found. */
  public markTargetMissing(): void {
    if (!this.targetMissingSource.value) {
      this.targetMissingSource.next(true);
    }
  }

  /** Forget the refusal. */
  public clear(): void {
    if (this.targetMissingSource.value) {
      this.targetMissingSource.next(false);
    }
  }

  /**
   * End the current view-as. The one way every "Stop impersonation" control does it (the banner, the account
   * menu, and a failure message's action), so a step added here reaches all of them. The warning goes at
   * once, rather than after `cancelImpersonation`'s navigations have finished.
   */
  public stopViewAs(): void {
    this.clear();
    this.userService.cancelImpersonation();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
